import FroalaEditor from 'froala-editor';

/**
 * Creates a custom Froala Editor plugin for specialized popups.
 *
 * This method provides a reusable way to register Froala plugins that display Angular components
 * in popup dialogs. It standardizes the creation, positioning, and cleanup of these popups.
 *
 * @param popupName - The name of the plugin to register in Froala (e.g., 'ttAnchorLinkPlugin')
 * @param idPrefix - Button prefix used in the toolbar command ID (e.g., 'add-anchor-link-button')
 * @param containerIdPrefix - HTML ID prefix for the container that will host the Angular component (found in utils)
 *
 * @remarks
 * The plugin creates standardized methods for showing/hiding popups and managing their lifecycle.
 * It automatically handles positioning relative to the toolbar button and proper cleanup of existing popups.
 */
export const createCustomFroalaPopup = (popupName: string, idPrefix: string, containerIdPrefix: string) => {
  FroalaEditor.POPUP_TEMPLATES[`${popupName}.popup`] = '[_CUSTOM_LAYER_]';

  FroalaEditor.PLUGINS[popupName] = function (editor) {
    function showPopup(attachComponentCallback: (editor: FroalaEditor) => void) {
      removeExistingPopups();

      const template = {
        custom_layer: `<div class="custom-layer" id="${containerIdPrefix}"></div>`,
      };

      editor.popups.create(`${popupName}.popup`, template);

      attachComponentCallback(editor);

      editor.popups.setContainer(`${popupName}.popup`, editor.$tb);

      const $btn = editor.$tb.find(`.fr-command[data-cmd="${idPrefix}"]`);

      const left = $btn.offset().left + $btn.outerWidth() / 2;
      const top = $btn.offset().top + (editor.opts.toolbarBottom ? 10 : $btn.outerHeight() - 10);

      editor.popups.show(`${popupName}.popup`, left, top, $btn.outerHeight());
    }

    function removeExistingPopups() {
      const existingPopup = document.getElementById(containerIdPrefix);
      if (!existingPopup) {
        return;
      }

      const parent = existingPopup.parentElement;
      if (parent?.classList.contains('fr-popup')) {
        parent.remove();
      }
    }

    return {
      showPopup,
    };
  };
};
